/**
 * @file src/offline/offlineCollectionBoot.js
 * @description What `?collection=<id>` means once a collection can be downloaded.
 *
 * It used to mean one thing: fetch the roster from mycelium. That is a sentence with a server in
 * it, and the whole point of a download is that there is no server. So it now means "read the
 * stored manifest, and fall back to the fetch only when none is stored".
 *
 * ## The manifest is for having no connection — it does not replace the collection
 *
 * With a link, the live roster wins. An earlier version booted the manifest whenever one existed,
 * so that a download always played exactly the releases it had kept; what that actually produced was
 * a collection frozen in time, where tracks added to it afterwards never showed up and people
 * deleted their download to get them back. Downloaded tracks still play from disk either way —
 * playback finds the pinned copy by track and version — so reading live costs nothing and keeps the
 * collection honest.
 *
 * A newer release of a track somebody kept is a separate fact, reported as "update available"
 * (`offlineUpdates.js`) and acted on by re-downloading. It is never a silent substitution of bytes.
 *
 * ## Booting from the manifest is NOT the same as being offline
 *
 * These were once one branch and that was a bug. Reading the stored releases is about playing the
 * bytes somebody kept; being cut off is about what may be written. An online visitor booting a
 * collection they had downloaded was made read-only, unable to sign in, edit or save — the owner of
 * a collection locked out of it for the crime of keeping it. Read-only is now entered only when
 * there is no connection, or when there was one and it did not answer.
 *
 * ## What is verified before the manifest is trusted
 *
 * That its bytes are still here. A manifest is a statement of intent and Cache Storage is the
 * statement of fact — a browser may reclaim an origin's storage whole, and it does not tell the
 * page. So the entries are checked against the bucket and a collection whose audio is gone is not
 * booted from the manifest; it falls through to the network, which is exactly what a person with a
 * connection wants and gives a person without one an honest failure instead of a silent one.
 */
import { readCollectionOfflineStatus } from './offlineLibrary.js';
import {
  RELEASE_KIND,
  readDownloadedCollection,
  readStoredRelease,
  releaseRecordKey,
} from './offlineLibraryStore.js';
import { toCacheRequestUrl } from './offlineKeys.js';
import { readPresentCacheKeys } from '../audio/playback/audioCacheStore.js';
import { enterOfflineReadOnly } from './offlineMode.js';
import { primeStoredDefaultPair } from './offlineDefaultPair.js';
import { canReachNetwork } from './offlineUpdates.js';
import { resolveCoverSrc } from './offlineCoverSource.js';

/** How long a downloaded collection waits for the live roster before playing what it has. */
const LIVE_READ_DEADLINE_MS = 6000;

/**
 * The promise, or a rejection once the deadline passes.
 *
 * The request itself is left to finish or fail on its own — there is nothing to cancel it with here,
 * and its answer is no longer wanted either way.
 */
function withDeadline(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('The collection read took too long.')), ms);
    }),
  ]);
}

/**
 * Which stored release row an entry boots from.
 *
 * A collection is not only tracks: an orbiter on a stage is an instrument with no audio file at all,
 * and a world is a place. Each kind boots from its OWN release, so a single hardcoded `track` key read
 * as "no release stored" for both of them — which the playability check below treats as unplayable.
 */
function entryReleaseKey(entry) {
  if (entry?.entityType === 'orbiter') {
    return releaseRecordKey({ kind: RELEASE_KIND.ORBITER, id: entry.orbiterId, version: entry.orbiterVersion });
  }
  if (entry?.entityType === 'world') {
    return releaseRecordKey({
      kind: RELEASE_KIND.WORLD,
      id: entry.entangledWorldId,
      version: entry.entangledWorldVersion,
    });
  }
  return releaseRecordKey({ kind: RELEASE_KIND.TRACK, id: entry?.trackId, version: entry?.trackVersion });
}

/** What an entry is called when something has to be said about it. */
function entryLabel(entry) {
  return entry?.trackId || entry?.orbiterId || entry?.entangledWorldId || entry?.voiceId || null;
}

/**
 * Turn a stored manifest into the shape `createCollectionApp` boots from.
 *
 * The roster entries carry `hydrated` — the release payload stored at download time — which the
 * collection app already knows what to do with: it primes the release caches, so every voice's
 * hydration is a warm hit instead of a request. And they carry `trackVersion`, which is what makes
 * the primed key the key the voice will actually ask for. Without the pin, a voice requests the
 * LIVE release, resolves `version: null`, misses the cache it was just handed, and reaches for a
 * network that is not there.
 *
 * `permissions` is passed IN rather than assumed. With a connection the server is still asked what
 * this person may do, because booting from a manifest says nothing about their rights; only when
 * nobody could be asked does it fall back to view-only.
 *
 * @param {object} manifest a stored manifest
 * @param {Map<string, object>} releases release payloads by record key
 * @param {Map<string, string>} [covers] drawable cover addresses by voiceId — see `resolveEntryCovers`
 * @returns {object} collectionData
 */
export function buildOfflineCollectionData(manifest, releases, covers = new Map(), permissions = null) {
  const roster = [];
  for (const entry of manifest?.entries || []) {
    const hydrated = releases.get(entryReleaseKey(entry));
    roster.push({
      voiceId: entry.voiceId,
      itemId: entry.itemId ?? null,
      trackId: entry.trackId,
      // The pin. See above — this is what makes the primed payload findable.
      trackVersion: entry.trackVersion ?? null,
      orbiterId: entry.orbiterId ?? null,
      orbiterVersion: entry.orbiterVersion ?? null,
      entangledWorldId: entry.entangledWorldId ?? null,
      entangledWorldVersion: entry.entangledWorldVersion ?? null,
      // What this stage IS. An orbiter or a world on its own is not a track and must not be described
      // as one: the collection app reads this to know what it is booting.
      entityType: entry.entityType ?? 'track',
      title: entry.title ?? null,
      subtitle: entry.subtitle ?? null,
      // The artwork, from the bucket. The download stores every track's cover — it is in the
      // manifest as a logical key — and dropping it here was the difference between a drawer full
      // of cards and a drawer full of letters. The key is a NAME, not an address, so it has to be
      // read out of the cache and wrapped before anything can draw it.
      image: covers.get(entry.voiceId) ?? null,
      isOfficial: entry.isOfficial === true,
      hydrated: hydrated ?? null,
    });
  }

  const offlineAssetBindings = [];
  const addStored = (asset) => {
    if (asset?.url && asset?.key) {
      offlineAssetBindings.push({ url: asset.url, key: asset.key, stored: true });
    }
  };
  for (const entry of manifest?.entries || []) {
    for (const asset of entry?.assets || []) {
      // The manifest asset row is already the exact identity selected when the bytes were
      // downloaded. Do not require the entry's derived ids/versions to be present again: older
      // manifests (and releases whose world version was supplied separately) can have a valid
      // texture row without those convenience fields, and dropping that binding makes the worker
      // fall back to a network fetch even though the bytes are in Cache Storage.
      addStored(asset);
    }
  }
  const defaultWorld = manifest?.defaultPair;
  if (defaultWorld?.worldId && defaultWorld?.worldVersion != null) {
    for (const asset of manifest?.assets || []) {
      if (asset?.kind === 'texture') addStored(asset);
    }
  }

  return {
    collectionId: manifest.collectionId,
    offlineAssetBindings,
    roster,
    // Edit is refused only when nobody could be ASKED. The cookie is on disk and unverifiable in
    // that case, so granting edit would be inventing an answer — but refusing it when the server
    // DID answer locks an owner out of their own collection.
    permissions: permissions ?? { canView: true, canEdit: false },
    // The arrangement as it was downloaded. Rearranging offline still works — it is session-local,
    // which is what it already is for anyone without edit permission — it simply cannot be saved.
    layout: manifest.layout ?? null,
    name: manifest.name ?? null,
    coverImage: manifest.coverImage ?? null,
    roomJoinCode: null,
    roomShape: null,
    roomScore: null,
    roomDraw: null,
  };
}

/**
 * Whether a stored manifest can actually be played right now.
 *
 * "Ready" is too strict a bar to boot on: a collection missing one track's cover art is entirely
 * playable, and refusing it would send someone with no connection to an error page over a
 * thumbnail. What cannot be missing is the two things a stage is built from — the AUDIO bytes, and
 * the hydrated RELEASE that names what plays them. They live in different stores and can be lost
 * independently, so both are checked.
 *
 * @returns {Promise<{playable: boolean, entries: object[], missingAudioTrackIds: string[]}>}
 */
export async function assessManifestPlayability(manifest) {
  const entries = manifest?.entries || [];
  const audioKeys = [];
  for (const entry of entries) {
    for (const identity of entry.audio || []) {
      const key = toCacheRequestUrl(identity);
      if (key) audioKeys.push(key);
    }
  }

  const present = await readPresentCacheKeys(audioKeys);
  const playableEntries = [];
  const missing = [];
  for (const entry of entries) {
    const keys = (entry.audio || []).map((identity) => toCacheRequestUrl(identity)).filter(Boolean);
    // An orbiter or a world on its own HAS no audio, which is a different fact from audio that is
    // missing. Judged on its release alone, as there is nothing else it needs.
    const wantsAudio = entry.entityType !== 'orbiter' && entry.entityType !== 'world';
    const hasAudio = !wantsAudio || (keys.length > 0 && keys.every((key) => present.has(key)));
    // Bytes are not enough. A voice boots from the HYDRATED RELEASE — the payload that names the
    // orbiter, the world and the audio assets — and the two live in different stores that can fail
    // independently. An entry whose audio survived while its release row did not was being counted
    // as playable, handed `hydrated: null`, and left reaching for a release over a network that is
    // not there. It is excluded instead, which is the same treatment as missing audio and for the
    // same reason: it cannot play.
    const releaseKey = entryReleaseKey(entry);
    const hasRelease = releaseKey
      ? Boolean(await readStoredRelease(releaseKey).catch(() => null))
      : false;
    if (hasAudio && hasRelease) playableEntries.push(entry);
    else missing.push(entryLabel(entry));
  }

  return { playable: playableEntries.length > 0, entries: playableEntries, missingAudioTrackIds: missing };
}

/**
 * Every entry's cover, as an address a card can draw.
 *
 * Resolved once, at boot, rather than per card: the cards are rebuilt as stages are arranged, and
 * minting a fresh object URL on every render would leak one blob per redraw. These live as long as
 * the session does, which for a page booted to play a collection is the right lifetime.
 *
 * A cover that is not in the bucket simply resolves to null and the card falls back to its initial,
 * exactly as it does for a track that never had artwork.
 *
 * @param {object[]} entries manifest entries
 * @returns {Promise<Map<string, string>>} voiceId → drawable src
 */
export async function resolveEntryCovers(entries) {
  const covers = new Map();
  for (const entry of entries || []) {
    if (!entry?.cover) continue;
    const { src } = await resolveCoverSrc(entry.cover, null);
    if (src) covers.set(entry.voiceId, src);
  }
  return covers;
}

/**
 * The collection to boot, from the manifest when there is one and the network otherwise.
 *
 * @param {string} collectionId
 * @param {{ fetchOnline: (id: string) => Promise<object>, allowStored?: boolean }} deps
 *   `allowStored` false forces the live read — a room link, where the pool and the grant's own
 *   fields cannot come from a manifest. The fetch is injected rather
 *   than imported so this module does not drag the whole API client into the offline page's bundle
 *   and so a test can assert "the fetch was not made" rather than intercept it.
 * @returns {Promise<{ collectionData: object, source: 'offline'|'network' }>}
 */
export async function resolveCollectionForBoot(collectionId, { fetchOnline, allowStored = true }) {
  // A ROOM is never booted from a manifest. `?collection=<id>&room=<id>` resolves to this same
  // boot, and a room's join code, deck shape, score and card draw are all minted onto the GRANT and
  // returned by the live read — none of them exist in a manifest. Taking the stored path there
  // dealt the downloaded roster instead of the room's pool, with no code and no shape, into a room
  // everybody else was playing differently. A room is a live thing; it needs the live answer.
  if (!allowStored) {
    return { collectionData: await fetchOnline(collectionId), source: 'network' };
  }

  const manifest = await readDownloadedCollection(collectionId).catch(() => null);
  const online = canReachNetwork();

  // WITH A CONNECTION, the collection is whatever the collection IS.
  //
  // This used to boot the manifest whenever one existed, on the reasoning that somebody who kept a
  // collection asked to keep THOSE releases. The consequence was a collection frozen at the roster
  // it was downloaded with: tracks added to it afterwards never appeared, and the only way anybody
  // found to see them was to delete the download and take it again. Keeping bytes is not a request
  // to stop being shown the collection.
  //
  // Nothing is lost by reading live. A track whose bytes are here still PLAYS from here — playback
  // resolves the pinned copy by track and version, so a downloaded track costs no network either
  // way, and a track added since simply streams like any other. The download's job is what happens
  // when there is no connection, and that is decided below.
  let liveReadFailed = false;
  if (manifest && online) {
    try {
      // Bounded, because `navigator.onLine` answers "is there a link" and not "does it lead
      // anywhere". On a captive portal or against a stalled backend the read never returns, and an
      // unbounded await would hold a collection that is sitting on this disk at "Loading…" — the one
      // situation a download exists for. Past the deadline this is the offline case.
      return {
        collectionData: await withDeadline(fetchOnline(collectionId), LIVE_READ_DEADLINE_MS),
        source: 'network',
      };
    } catch {
      // There was a link and it did not answer. Remembered rather than acted on yet: if the manifest
      // turns out to be playable this IS the offline case, and if it does not, the read-only latch
      // and a second attempt at the request that just failed would both be wrong.
      liveReadFailed = true;
    }
  }

  if (manifest) {
    const { playable, entries, missingAudioTrackIds } = await assessManifestPlayability(manifest);

    if (playable) {
      if (missingAudioTrackIds.length) {
        console.warn(
          '[offline] Booting a downloaded collection with missing tracks; their bytes are gone from this browser.',
          missingAudioTrackIds,
        );
      }
      const covers = await resolveEntryCovers(entries);
      const releases = new Map();
      for (const entry of entries) {
        const key = entryReleaseKey(entry);
        const payload = key ? await readStoredRelease(key).catch(() => null) : null;
        if (payload) releases.set(key, payload);
      }

      // The account's fallback orbiter and world, before any voice asks for one. A track whose
      // orbiter has gone away falls back to this pair, and so does every file imported from this
      // machine; with no connection the chain that finds it ends at the in-app constants and a
      // request for them that cannot succeed, which draws a plain sphere.
      await primeStoredDefaultPair();

      // Reached only with no connection, or with one that did not answer — both of which forbid
      // writing, because a session that could not be checked must not report itself to the server.
      enterOfflineReadOnly();

      return {
        collectionData: buildOfflineCollectionData(
          { ...manifest, entries, layout: manifest.layout ?? null },
          releases,
          covers,
          null,
        ),
        source: 'offline',
      };
    }
    console.warn(
      '[offline] A manifest for this collection is stored but its bytes are not here; falling back to the network.',
      collectionId,
    );
    // The live read already failed once and the manifest cannot stand in for it. Asking again would
    // be the same request with the same answer, so let the caller see the failure.
    if (liveReadFailed) throw new Error('This collection could not be read, and what is stored cannot play.');
  }

  return { collectionData: await fetchOnline(collectionId), source: 'network' };
}

/**
 * Whether a downloaded collection has been superseded online.
 *
 * Answers by comparing the PINNED track versions in the manifest with what the roster says is live.
 * Never re-downloads and never rewrites the manifest's versions: an update is a thing to be told
 * about and then chosen, and swapping bytes somebody asked to keep is the one move this whole
 * feature exists to avoid.
 *
 * @param {object} manifest
 * @param {object} liveCollectionData a freshly fetched `normalizeCollectionPayload` result
 * @returns {boolean}
 */
export function hasUpdateAvailable(manifest, liveCollectionData) {
  const live = new Map();
  for (const entry of liveCollectionData?.roster || []) {
    if (!entry?.trackId) continue;
    // The roster normalizer does NOT carry a version (`collectionDataService.normalizeRosterEntry`);
    // the version lives on the hydrated release each entry brings with it. Reading `trackVersion`
    // off the roster resolved undefined for every entry, so this answered "no update" always — and
    // a test that fabricated the field on the way in happily agreed with it.
    const version = entry.hydrated?.release?.version ?? entry.trackVersion ?? null;
    live.set(entry.trackId, version);
  }

  for (const entry of manifest?.entries || []) {
    const liveVersion = live.get(entry.trackId);
    // A track that is no longer in the collection is not an update — it is a removal, and the
    // downloaded copy is still exactly what was asked for.
    if (liveVersion === undefined || liveVersion === null) continue;
    if (Number(liveVersion) > Number(entry.trackVersion)) return true;
  }
  return false;
}

/** Re-export so the boot has one import for "is this collection here, and how much of it". */
export { readCollectionOfflineStatus };
